import { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Clock, MapPin, Users, Wifi, Snowflake, Zap, ChevronRight, Filter, ArrowUpDown, Coffee, AlertTriangle, Bus, Armchair, Search, Calendar, ArrowRight } from 'lucide-react';
import Footer from '@/components/Footer';
import SearchForm from '@/components/SearchForm';
import { searchTrips, BusResult } from '@/services/routeService';
import { getToday } from '@/lib/utils';
import SearchResultsSkeleton from '@/components/SearchResultsSkeleton';
import PageHead from '@/components/PageHead';
import { useLanguage } from '@/contexts/LanguageContext';
import CustomSelect from '@/components/ui/CustomSelect';
import { formatDisplayNumber, formatTime12 } from '@/lib/formatters';

/** Format YYYY-MM-DD → DD-MM-YYYY for display */
function formatDateDMY(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}

const amenityIcons: Record<string, typeof Wifi> = {
  'AC': Snowflake, 'WiFi': Wifi, 'USB Charging': Zap, 'Snacks': Coffee, 'Fan': Snowflake,
};

export default function SearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Defaults must name a route Star Line actually operates. These previously
  // read Dhaka → Chattogram, which has no row in `routes` at all, so every
  // customer arriving via a bare /search link (the homepage hero CTA, the CTA
  // band and the nav all point there) hit an immediate "No Trips Found".
  const from = params.get('from') || 'Feni';
  const to = params.get('to') || 'Maniknagar';
  const date = params.get('date') || getToday();
  const passengers = Number(params.get('passengers') || 1);

  const [sortBy, setSortBy] = useState<'price' | 'departure'>('departure');
  const [filterType, setFilterType] = useState<string>('all');
  const { lang, t } = useLanguage();

  // useQuery: auto-caches results, deduplicates on back-navigation, respects QueryClient staleTime
  const { data: results = [], isLoading: loading } = useQuery<BusResult[]>({
    queryKey: ['searchTrips', from, to, date],
    queryFn: () => searchTrips(from, to, date),
  });

  const filtered = useMemo(() => {
    let r = filterType === 'all' ? results : results.filter(b => b.coachType === filterType);
    if (sortBy === 'price') r = [...r].sort((a, b) => a.fare - b.fare);
    if (sortBy === 'departure') r = [...r].sort((a, b) => a.departureTime.localeCompare(b.departureTime));
    return r;
  }, [results, sortBy, filterType]);

  const acCount = results.filter(b => b.coachType === 'AC').length;
  const nonAcCount = results.filter(b => b.coachType === 'Non-AC').length;

  const selectBus = (bus: BusResult) => {
    navigate(`/seat-selection?scheduleId=${bus.scheduleId}&busId=${bus.id}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&date=${date}&passengers=${passengers}&fare=${bus.fare}&coachName=${encodeURIComponent(bus.coachName)}&coachType=${encodeURIComponent(bus.coachType)}&dep=${bus.departureTime}&arr=${bus.arrivalTime}&duration=${encodeURIComponent(bus.duration)}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHead title={`${from} to ${to} Buses`} description={`Find and book buses from ${from} to ${to}. Compare fares, timings, and seat availability.`} />
      <div className="pt-4 pb-12">
        <div className="container">
          <SearchForm variant="compact" initialFrom={from} initialTo={to} initialDate={date} initialPassengers={passengers} />

          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mt-8 mb-6">
            <div>
              <h1 className="font-display text-2xl font-bold">{from} → {to}</h1>
              <p className="text-sm text-muted-foreground">
                {formatDisplayNumber(filtered.length, lang)} {t('trips_available')} • {formatDateDMY(date)} • {formatDisplayNumber(acCount, lang)} AC, {formatDisplayNumber(nonAcCount, lang)} Non-AC
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <CustomSelect
                  variant="compact"
                  title="Filter by class"
                  value={filterType}
                  onChange={setFilterType}
                  options={[
                    { value: 'all', label: 'All Types' },
                    { value: 'AC', label: `AC Only (${acCount})` },
                    { value: 'Non-AC', label: `Non-AC Only (${nonAcCount})` },
                  ]}
                />
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                <CustomSelect
                  variant="compact"
                  title="Sort results"
                  value={sortBy}
                  onChange={v => setSortBy(v as 'departure' | 'price')}
                  options={[
                    { value: 'departure', label: 'Departure' },
                    { value: 'price', label: 'Price' },
                  ]}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <SearchResultsSkeleton />
          ) : filtered.length === 0 ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-secondary/60 flex items-center justify-center mx-auto mb-5">
                <Bus className="w-7 h-7 text-muted-foreground" />
              </div>
              <h3 className="font-display text-xl font-bold mb-2">{t('no_trips')}</h3>
              <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">
                {t('no_trips_desc')}
                {filterType !== 'all' && ` ${t('show_all_types')}.`}
              </p>

              {filterType !== 'all' && (
                <button
                  onClick={() => setFilterType('all')}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors mb-6"
                >
                  <Search className="w-4 h-4" /> Show All Types
                </button>
              )}

              {/* Suggest nearby dates */}
              <div className="mb-6">
                <p className="text-xs text-muted-foreground mb-3">{t('try_nearby_dates')}</p>
                <div className="flex justify-center gap-2 flex-wrap">
                  {[-1, 1, 2].map(offset => {
                    const d = new Date(date);
                    d.setDate(d.getDate() + offset);
                    const altDate = d.toISOString().split('T')[0];
                    if (altDate < getToday()) return null;
                    return (
                      <Link
                        key={offset}
                        to={`/search?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&date=${altDate}&passengers=${passengers}`}
                        className="flex items-center gap-1.5 bg-secondary px-3 py-2 rounded-lg text-xs font-medium text-foreground hover:bg-secondary/80 transition-colors"
                      >
                        <Calendar className="w-3 h-3" /> {formatDateDMY(altDate)}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="flex flex-col gap-4">
              {filtered.map((bus, i) => {
                const lowSeats = bus.availableSeats <= 8;
                return (
                <motion.div
                  key={bus.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="glass-card p-5 card-hover"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Left: Coach + Time */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-sm font-semibold text-primary">{bus.coachName}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          bus.coachType === 'AC'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-secondary text-muted-foreground'
                        }`}>
                          {bus.coachType}
                        </span>
                        {lowSeats && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive font-medium flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {bus.availableSeats} seats left
                          </span>
                        )}
                      </div>

                      {/* Time row */}
                      <div className="flex items-center gap-4 mt-3">
                        <div className="text-center min-w-[56px]">
                          <div className="font-display text-xl font-bold">{formatTime12(bus.departureTime, lang)}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[80px]">{from}</div>
                        </div>
                        <div className="flex-1 flex flex-col items-center">
                          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {bus.duration}
                          </div>
                          <div className="w-full h-px bg-border relative">
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary" />
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-accent" />
                          </div>
                        </div>
                        <div className="text-center min-w-[56px]">
                          <div className="font-display text-xl font-bold">{formatTime12(bus.arrivalTime, lang)}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[80px]">{to}</div>
                        </div>
                      </div>

                      {/* Boarding/Dropping + Amenities */}
                      {/* Boarding is the first pickup, but the drop-off shown must be
                          the terminus — this used to render droppingPoints[0], so a
                          Feni → Abdullahpur trip advertised dropping at "Demra Staff
                          Quarter Counter" (sort_order 6) while seat selection
                          correctly defaulted to Abdullahpur. The two pages disagreed. */}
                      <div className="flex flex-col sm:flex-row gap-3 mt-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-primary" />
                            {bus.boardingPoints[0]}
                            {bus.boardingPoints.length > 1 && (
                              <span className="text-muted-foreground/70">+{bus.boardingPoints.length - 1}</span>
                            )}
                          </span>
                          <ArrowRight className="w-3 h-3 hidden sm:block" />
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-accent-strong" />
                            {bus.droppingPoints[bus.droppingPoints.length - 1]}
                            {bus.droppingPoints.length > 1 && (
                              <span className="text-muted-foreground/70">+{bus.droppingPoints.length - 1}</span>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {bus.amenities.slice(0, 4).map(a => {
                          const Icon = amenityIcons[a] || Clock;
                          return (
                            <span key={a} className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Icon className="w-3 h-3" /> {a}
                            </span>
                          );
                        })}
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Armchair className="w-3 h-3" /> {bus.totalSeats} seats
                        </span>
                      </div>
                    </div>

                    {/*
                      Right: Price + CTA.

                      On phones the fare sits on its own line and the button
                      goes full width. It used to be a right-aligned pill, which
                      put it directly under the floating chat launcher at 360px
                      — the launcher covered the primary booking action on every
                      card that scrolled to that height. Full width keeps most
                      of the button clear of it, and is the better tap target
                      regardless.
                    */}
                    <div className="flex flex-col gap-3 border-t border-border/50 pt-4 mt-4 sm:flex-row sm:items-center sm:justify-between md:border-0 md:pt-0 md:mt-0 md:w-auto md:flex-col md:items-end md:gap-2">
                      <div className="flex items-center gap-2 sm:flex-col sm:items-start md:items-end">
                        <div className="font-display text-xl md:text-2xl font-bold text-accent-strong">৳{bus.fare}</div>
                        {!lowSeats && (
                          <div className="flex items-center gap-1 text-xs md:text-sm text-muted-foreground sm:order-first">
                            <Users className="w-3.5 md:w-4 h-3.5 md:h-4" />
                            <span>{bus.availableSeats} seats</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => selectBus(bus)}
                        className="w-full sm:w-auto bg-primary text-primary-foreground rounded-lg px-4 md:px-5 py-3 sm:py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors btn-primary-glow flex items-center justify-center gap-1 min-w-[120px] whitespace-nowrap"
                      >
                        {t('select_seats')} <ChevronRight className="w-4 h-4 hidden sm:inline" />
                      </button>
                    </div>
                  </div>
                </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
